// Manual deck editor — /decks/:id/edit.
//
// Three-region shell from Plans/DECK-EDITING.md. The left chat pane is
// intentionally absent in v1 so the layout can accommodate it later
// without restructuring.
//
// Local state is the pending deck (mainboard + sideboard). Server
// truth is the saved deck loaded via getDeck. Save writes through
// updateDeck and returns the post-update detail, which we drop into
// the react-query cache so DeckDetail picks up the edit without a
// refetch.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, MessageSquare, Plus, Minus, Upload, X, Search as SearchIcon } from "lucide-react";
import {
  DeckEditValidationError,
  extractAskEdits,
  getCardMetaBatch,
  getDeck,
  searchCards,
  updateDeck,
  validateDeckEdit,
  type CardMeta,
  type CardSummary,
  type Deck,
  type DeckEntry,
  type DeckValidationResult,
  type SavedDeckDetail,
} from "../lib/api";
import ManaCurve from "../components/ManaCurve";
import { cn } from "../lib/cn";
import CardHover from "../components/CardHover";
import ManaCost from "../components/ManaCost";
import SetSymbol from "../components/SetSymbol";
import { parseArenaDeckText } from "../lib/arena-format";
import { cardCopyLimit } from "../../../src/validator/copy-limit.ts";
import { cacheSavedDeck } from "../lib/deck-cache";

// Debounce helper — matches the one in CardSearch.tsx. Inlined rather
// than pulled into a shared hooks file to keep this PR surgical; extract
// if a third caller appears.
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// Same type-ordering rules DeckView uses. Duplicated locally because
// the editor needs to call bucketFor on every mutation and the DeckView
// helper isn't exported. Keep in sync — if you reorder these, reorder
// there too.
const TYPE_ORDER = [
  "Creature",
  "Planeswalker",
  "Instant",
  "Sorcery",
  "Artifact",
  "Enchantment",
  "Battle",
  "Land",
] as const;
type BucketTitle = (typeof TYPE_ORDER)[number] | "Other";

function bucketFor(
  name: string,
  meta: Record<string, CardMeta> | undefined,
): BucketTitle {
  const m = meta?.[name.toLowerCase()];
  if (!m) return "Other";
  for (const t of TYPE_ORDER) {
    if (m.types.includes(t)) return t;
  }
  const tl = m.type_line || "";
  for (const t of TYPE_ORDER) {
    if (tl.includes(t)) return t;
  }
  return "Other";
}

function pluralize(title: BucketTitle): string {
  if (title === "Other") return "Other";
  if (title === "Sorcery") return "Sorceries";
  return `${title}s`;
}

function sum(entries: DeckEntry[]): number {
  return entries.reduce((s, e) => s + e.count, 0);
}

function entriesEqual(a: DeckEntry[], b: DeckEntry[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const x = a[i]!;
    const y = b[i]!;
    if (x.name !== y.name || x.count !== y.count) return false;
    if ((x.set ?? null) !== (y.set ?? null)) return false;
    if ((x.collector_number ?? null) !== (y.collector_number ?? null)) return false;
  }
  return true;
}

function deckEqual(a: Deck, b: Deck): boolean {
  return (
    entriesEqual(a.mainboard, b.mainboard) &&
    entriesEqual(a.sideboard, b.sideboard)
  );
}

// Decks built via /draft are stored as format: "standard" but are
// actually 40-card Limited builds. Client-side we infer Limited from
// the *current* mainboard size — same threshold the server uses.
// Standard floor is 60, Limited target is 40, so anything below 50 is
// Limited.
const LIMITED_MAINBOARD_THRESHOLD = 50;

function isLimitedDeck(deck: Deck): boolean {
  const main = sum(deck.mainboard);
  return main > 0 && main < LIMITED_MAINBOARD_THRESHOLD;
}

// Cheap client-side sanity check. Catches most mistakes instantly;
// server /validate is the source of truth at save time. Standard
// decks need ≥60 main, ≤15 side, and Oracle-aware copy limits; Limited decks need
// exactly 40 main with no sideboard cap and no 4-copy rule (the
// pool itself is the constraint).
function cheapLegalityHint(deck: Deck, meta: Record<string, CardMeta> | undefined): { ok: boolean; errors: string[] } {
  const errors: string[] = [];
  const main = sum(deck.mainboard);
  const side = sum(deck.sideboard);
  if (isLimitedDeck(deck)) {
    if (main !== 40) {
      errors.push(`Mainboard has ${main} cards (Limited requires exactly 40).`);
    }
    // No sideboard cap, no 4-copy rule for Limited.
    return { ok: errors.length === 0, errors };
  }
  if (main < 60) errors.push(`Mainboard has ${main} cards (min 60).`);
  if (side > 15) errors.push(`Sideboard has ${side} cards (max 15).`);
  const counts = new Map<string, number>();
  for (const e of [...deck.mainboard, ...deck.sideboard]) {
    const key = e.name.toLowerCase();
    counts.set(key, (counts.get(key) ?? 0) + e.count);
  }
  for (const [name, n] of counts) {
    const card = meta?.[name.toLowerCase()];
    const limit = cardCopyLimit(card ?? { name }, deck.format);
    if (card && n > limit) {
      errors.push(`"${name}" has ${n} copies (max ${limit}).`);
    }
  }
  return { ok: errors.length === 0, errors };
}

type Section = "main" | "side";

export default function DeckEdit() {
  // Route is `/decks/:owner/:slug/edit`. We read `slug` as the deck id to
  // minimize callsite churn inside this file (the rest of the file
  // already references `id`). `owner` comes straight from the URL so
  // we can build the back/save navigation links without waiting on the
  // deck fetch.
  const { owner = "", slug = "" } = useParams();
  const id = slug;
  const ownerUsername = owner;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  // ?from_ask=<ask_id> — set by the Apply CTA on DeckDetail. When
  // present we hydrate the editor's draft from the extractor's
  // proposed_deck instead of the saved deck, AND thread the ask id
  // through to PUT so the version row links the originating ask and
  // the upload-rate charge fires server-side.
  const fromAskId = searchParams.get("from_ask");

  const { data, isLoading, error } = useQuery({
    queryKey: ["deck", id],
    queryFn: () => getDeck(id),
    enabled: !!id,
  });

  // Fetch the extracted edits as soon as the page mounts with
  // ?from_ask. This is a Sonnet call so we let it run in the
  // background while the user reads the saved deck. Cached forever —
  // the answer is immutable once persisted.
  const extractedQuery = useQuery({
    queryKey: ["ask-extracted-edits", id, fromAskId],
    queryFn: () => extractAskEdits(id, fromAskId!),
    enabled: !!id && !!fromAskId,
    staleTime: Infinity,
    retry: 1,
  });

  // Local pending deck. Initialized once from the server-loaded deck;
  // we intentionally do NOT reset when `data` changes while the editor
  // is open — that would discard in-progress edits if a background
  // refetch lands mid-session. Reset only via the Discard button.
  const [draft, setDraft] = useState<Deck | null>(null);
  // Tracks whether we've injected the from_ask extractor's proposed
  // deck. Without this guard, a background refetch of the extractor
  // (e.g. on focus return) would overwrite the user's in-progress
  // tweaks to the proposed list.
  const [askProposalApplied, setAskProposalApplied] = useState(false);
  // True after the user pasted a fresh Arena list over the draft via
  // the Upload modal. Threaded through to PUT as `from_upload` so the
  // resulting version row is labeled source='upload' instead of
  // 'manual_edit'. Cleared on successful save (via navigate-away) and
  // on Discard (which restores the saved deck and undoes the upload).
  const [pendingUpload, setPendingUpload] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  useEffect(() => {
    if (data?.deck && !draft) {
      setDraft({
        format: data.deck.format,
        mainboard: data.deck.mainboard.map((e) => ({ ...e })),
        sideboard: data.deck.sideboard.map((e) => ({ ...e })),
      });
    }
  }, [data?.deck, draft]);

  // Inject the extractor's proposed_deck on top of the saved deck
  // once both have loaded. Only fires once per session (askProposalApplied
  // guard) so a refetch doesn't clobber user edits. If the extractor
  // produced zero edits we leave the saved deck in place — the
  // unmapped note is surfaced separately.
  useEffect(() => {
    if (askProposalApplied) return;
    if (!fromAskId) return;
    if (!draft) return;
    if (!extractedQuery.data) return;
    const ex = extractedQuery.data;
    if (ex.edits.length === 0) {
      setAskProposalApplied(true);
      return;
    }
    setDraft({
      format: ex.proposed_deck.format,
      mainboard: ex.proposed_deck.mainboard.map((e) => ({ ...e })),
      sideboard: ex.proposed_deck.sideboard.map((e) => ({ ...e })),
    });
    setAskProposalApplied(true);
  }, [fromAskId, draft, extractedQuery.data, askProposalApplied]);

  const dirty = useMemo(() => {
    if (!draft || !data?.deck) return false;
    return !deckEqual(draft, data.deck);
  }, [draft, data?.deck]);

  // beforeunload guard — the editor is lossy on accidental navigation.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // Batch card metadata lookup — drives curve, colors, lands, bucket.
  const allNames = useMemo(() => {
    if (!draft) return [] as string[];
    const set = new Set<string>();
    for (const e of draft.mainboard) set.add(e.name);
    for (const e of draft.sideboard) set.add(e.name);
    return [...set];
  }, [draft]);
  const metaQuery = useQuery({
    queryKey: ["card-meta-batch", allNames],
    queryFn: () => getCardMetaBatch(allNames),
    enabled: allNames.length > 0,
  });

  // Server-validated legality, debounced so every keystroke doesn't
  // round-trip. The cheap client-side hint fills the gap during the
  // debounce window.
  const debouncedDraft = useDebounced(draft, 500);
  const validationQuery = useQuery({
    queryKey: [
      "deck-validate",
      id,
      debouncedDraft?.mainboard,
      debouncedDraft?.sideboard,
    ],
    queryFn: () =>
      validateDeckEdit(id, {
        mainboard: debouncedDraft!.mainboard,
        sideboard: debouncedDraft!.sideboard,
      }),
    enabled: !!debouncedDraft && dirty,
    staleTime: 30_000,
  });
  const cheapHint = draft ? cheapLegalityHint(draft, metaQuery.data) : null;

  // ── mutations ──────────────────────────────────────────────────
  const saveMut = useMutation({
    mutationFn: async (): Promise<SavedDeckDetail> => {
      if (!draft) throw new Error("no draft");
      return updateDeck(id, {
        deck: {
          mainboard: draft.mainboard,
          sideboard: draft.sideboard,
        },
        // Apply-Ask save: server validates the ask belongs to the
        // caller and writes a version row with source='apply_ask'
        // + ask_id. Free at save like manual edits — strategy regen
        // is a separate user-initiated action.
        ...(fromAskId ? { from_ask_id: fromAskId } : {}),
        // Upload save: pasted-list mass-edit. Mutually exclusive with
        // from_ask_id (server enforces). Same free-at-save semantics.
        ...(pendingUpload && !fromAskId ? { from_upload: true } : {}),
      });
    },
    onSuccess: async (saved) => {
      await cacheSavedDeck(qc, ownerUsername, id, saved);
      void qc.invalidateQueries({ queryKey: ["decks"] });
      // The save wrote a new deck_versions row inside the same
      // transaction; invalidate so the VersionTimeline on the deck
      // detail page picks it up immediately on navigation.
      void qc.invalidateQueries({ queryKey: ["deck-versions", id] });
      navigate(`/decks/${ownerUsername}/${id}`);
    },
  });

  // ── mutators ───────────────────────────────────────────────────
  const mutate = (section: Section, fn: (arr: DeckEntry[]) => DeckEntry[]) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const nextMain = section === "main" ? fn(prev.mainboard) : prev.mainboard;
      const nextSide = section === "side" ? fn(prev.sideboard) : prev.sideboard;
      return { ...prev, mainboard: nextMain, sideboard: nextSide };
    });
  };

  const changeCount = (section: Section, idx: number, delta: number) => {
    mutate(section, (arr) => {
      const next = arr.slice();
      const e = next[idx];
      if (!e) return next;
      const n = e.count + delta;
      if (n <= 0) next.splice(idx, 1);
      else next[idx] = { ...e, count: n };
      return next;
    });
  };

  const removeEntry = (section: Section, idx: number) => {
    mutate(section, (arr) => arr.filter((_, i) => i !== idx));
  };

  const addCard = (
    section: Section,
    card: { name: string; set?: string; collector_number?: string },
    count = 1,
  ) => {
    mutate(section, (arr) => {
      const i = arr.findIndex((e) => e.name === card.name);
      if (i >= 0) {
        const e = arr[i]!;
        const next = arr.slice();
        // Merge by name only. Preserve the existing entry's printing if it
        // already has one; backfill from the new card if it doesn't.
        next[i] = {
          ...e,
          count: e.count + count,
          set: e.set ?? card.set,
          collector_number: e.collector_number ?? card.collector_number,
        };
        return next;
      }
      const entry: DeckEntry = { name: card.name, count };
      if (card.set) entry.set = card.set;
      if (card.collector_number) entry.collector_number = card.collector_number;
      return [...arr, entry];
    });
  };

  const discard = () => {
    if (!data?.deck) return;
    setDraft({
      format: data.deck.format,
      mainboard: data.deck.mainboard.map((e) => ({ ...e })),
      sideboard: data.deck.sideboard.map((e) => ({ ...e })),
    });
    setPendingUpload(false);
  };

  const applyUpload = (parsed: Deck) => {
    setDraft({
      format: parsed.format,
      mainboard: parsed.mainboard.map((e) => ({ ...e })),
      sideboard: parsed.sideboard.map((e) => ({ ...e })),
    });
    setPendingUpload(true);
    setUploadOpen(false);
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-10">
        <div className="skeleton h-16 w-full" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-10">
        <div className="surface p-4 text-sm text-danger">
          {(error as Error).message}
        </div>
      </div>
    );
  }
  if (!data || !draft) return null;

  const mainTotal = sum(draft.mainboard);
  const sideTotal = sum(draft.sideboard);
  // Format is inferred from the originally-loaded deck so deleting
  // cards down to 39 in a Standard deck doesn't flip the targets to
  // Limited mid-edit. The server uses the same threshold.
  const limitedDeck = data.deck ? isLimitedDeck(data.deck) : false;
  const mainTarget = limitedDeck ? 40 : 60;
  const sideTarget = limitedDeck ? null : 15;

  // React-query's cache keys by (id + mainboard + sideboard), so
  // `validationQuery.data` always matches the current debounced draft
  // once it resolves. `isFetching` tells us the server hasn't finished
  // checking yet — fall back to the cheap client hint during that
  // window to avoid a green/red flicker on every keystroke.
  const serverFresh = !!validationQuery.data && !validationQuery.isFetching;
  const liveErrors = serverFresh
    ? validationQuery.data!.errors
    : (cheapHint?.errors ?? []);

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-8 py-8 space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <Link
          to={`/decks/${ownerUsername}/${id}`}
          className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.75} />
          Back to deck
        </Link>
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-mono text-fg-subtle">
            {dirty ? "unsaved changes" : "no changes"}
          </span>
          <button
            className="btn-secondary text-sm h-9 px-4"
            onClick={discard}
            disabled={!dirty || saveMut.isPending}
          >
            Discard
          </button>
          <button
            className="btn-primary text-sm h-9 px-4"
            onClick={() => saveMut.mutate()}
            disabled={!dirty || saveMut.isPending || liveErrors.length > 0}
            title={
              liveErrors.length > 0
                ? "Fix legality errors before saving"
                : undefined
            }
          >
            {saveMut.isPending ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <header className="space-y-1">
        <div className="eyebrow">Editing</div>
        <h1 className="font-display text-2xl md:text-3xl font-medium tracking-[-0.01em]">
          {data.theme}
        </h1>
      </header>

      {fromAskId && (
        <ApplyAskBanner
          loading={extractedQuery.isLoading}
          error={extractedQuery.error as Error | null}
          editsCount={extractedQuery.data?.edits.length ?? 0}
          unmapped={extractedQuery.data?.unmapped ?? ""}
        />
      )}

      {saveMut.error && (
        <div className="surface p-4 text-sm text-danger space-y-1">
          <div className="font-medium">Could not save:</div>
          {saveMut.error instanceof DeckEditValidationError ? (
            <ul className="list-disc pl-5">
              {saveMut.error.errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          ) : (
            <div>{(saveMut.error as Error).message}</div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 items-start">
        {/* Deck panel */}
        <div className="space-y-6 min-w-0">
          <div className="flex items-stretch gap-2">
            <div className="flex-1 min-w-0">
              <AddCardBar
                onAdd={(card, section) => addCard(section, card)}
              />
            </div>
            <button
              type="button"
              onClick={() => setUploadOpen(true)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md border border-border",
                "bg-bg-raised/50 hover:bg-bg-raised text-fg-muted hover:text-fg",
                "px-3 text-xs font-medium transition-colors shrink-0",
              )}
              title="Paste an Arena-format decklist to replace the entire mainboard and sideboard."
              disabled={saveMut.isPending}
            >
              <Upload size={13} strokeWidth={1.75} />
              Upload list
            </button>
          </div>
          {pendingUpload && (
            <div className="surface p-3 text-xs text-accent inline-flex items-center gap-2">
              <Upload size={13} strokeWidth={1.75} />
              Pasted list staged. Save to record a new version
              (source: upload).
            </div>
          )}

          <DeckSection
            title="Mainboard"
            total={mainTotal}
            target={mainTarget}
            entries={draft.mainboard}
            meta={metaQuery.data}
            section="main"
            onDelta={changeCount}
            onRemove={removeEntry}
          />

          <DeckSection
            title="Sideboard"
            total={sideTotal}
            target={sideTarget}
            entries={draft.sideboard}
            meta={metaQuery.data}
            section="side"
            onDelta={changeCount}
            onRemove={removeEntry}
          />
        </div>

        {/* Right rail */}
        <aside className="space-y-4 lg:sticky lg:top-4">
          <LiveStats
            draft={draft}
            meta={metaQuery.data}
            serverValidation={validationQuery.data}
            cheapErrors={cheapHint?.errors ?? []}
            serverFresh={serverFresh}
            limited={limitedDeck}
          />
        </aside>
      </div>

      {uploadOpen && (
        <UploadListModal
          onClose={() => setUploadOpen(false)}
          onApply={applyUpload}
        />
      )}
    </div>
  );
}

// ── Upload-list modal ────────────────────────────────────────────
// Replaces the draft mainboard/sideboard with an Arena-format paste.
// Server validates legality at save time (PUT /api/decks/:id), so we
// only do a client-side parse here — empty parse blocks Apply.
function UploadListModal({
  onClose,
  onApply,
}: {
  onClose: () => void;
  onApply: (deck: Deck) => void;
}) {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleApply = () => {
    const parsed = parseArenaDeckText(text.trim());
    if (!parsed) {
      setError(
        "Couldn't parse a decklist. Use Arena format — `4 Lightning Bolt` per line, with `Deck` / `Sideboard` headers.",
      );
      return;
    }
    onApply(parsed);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="surface w-full max-w-2xl p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-baseline justify-between">
          <h2 className="font-display text-xl font-medium tracking-[-0.01em]">
            Upload list
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-fg-subtle hover:text-fg transition-colors"
            aria-label="Close upload modal"
          >
            <X size={16} strokeWidth={1.75} />
          </button>
        </header>
        <p className="text-xs text-fg-muted leading-relaxed">
          Paste an Arena-format decklist to replace the entire mainboard
          and sideboard with a new version. Save creates a new version
          row labeled <span className="font-mono">upload</span>; legality
          is validated server-side at save time.
        </p>
        <textarea
          className="input w-full min-h-[280px] font-mono text-[13px] leading-relaxed resize-y"
          placeholder={
            "Deck\n4 Monstrous Rage (MKM) 140\n4 Slickshot Show-Off (OTJ) 146\n...\n\nSideboard\n2 Torch the Tower (OTJ) 155\n..."
          }
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (error) setError(null);
          }}
          spellCheck={false}
          autoFocus
        />
        {error && (
          <div className="text-[11px] text-danger">{error}</div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-secondary text-sm h-9 px-4"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary text-sm h-9 px-4"
            onClick={handleApply}
            disabled={text.trim().length === 0}
          >
            Stage list
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Add-card search bar ──────────────────────────────────────────

function AddCardBar({
  onAdd,
}: {
  onAdd: (card: CardSummary, section: Section) => void;
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const debounced = useDebounced(q.trim(), 220);
  const containerRef = useRef<HTMLDivElement>(null);

  const searchQ = useQuery({
    queryKey: ["deck-edit-search", debounced],
    queryFn: () => searchCards(debounced, 10),
    enabled: debounced.length >= 2,
  });

  // Close the popover on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const pick = (card: CardSummary, section: Section) => {
    onAdd(card, section);
    setQ("");
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <SearchIcon
          size={14}
          strokeWidth={1.75}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle"
        />
        <input
          className="input pl-9 py-2 text-sm w-full"
          placeholder="Add a card by name or oracle text…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
      </div>
      {open && debounced.length >= 2 && (
        <div className="absolute left-0 right-0 top-full mt-1 z-20 surface max-h-80 overflow-y-auto p-1">
          {searchQ.isFetching && (
            <div className="px-3 py-2 text-xs text-fg-subtle">Searching…</div>
          )}
          {searchQ.data?.hits.length === 0 && !searchQ.isFetching && (
            <div className="px-3 py-2 text-xs text-fg-subtle">No matches.</div>
          )}
          {searchQ.data?.hits.map((c) => (
            <div
              key={c.oracle_id}
              className="group grid grid-cols-[1fr_auto_auto] items-center gap-2 px-2 py-1.5 rounded hover:bg-bg-raised/50"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">
                  <CardHover name={c.name}>{c.name}</CardHover>
                </div>
                <div className="text-[11px] text-fg-subtle truncate">
                  {c.type_line}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <ManaCost cost={c.mana_cost} />
              </div>
              <div className="flex items-center gap-1">
                <button
                  className="btn-secondary text-[11px] h-7 px-2"
                  onClick={() => pick(c, "main")}
                  title="Add to mainboard"
                >
                  + Main
                </button>
                <button
                  className="btn-secondary text-[11px] h-7 px-2"
                  onClick={() => pick(c, "side")}
                  title="Add to sideboard"
                >
                  + Side
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Deck section (main / side) ───────────────────────────────────

function DeckSection({
  title,
  total,
  target,
  entries,
  meta,
  section,
  onDelta,
  onRemove,
}: {
  title: string;
  total: number;
  /** Target count to display (e.g. 60 for Standard mainboard, 40 for
   *  Limited mainboard, 15 for Standard sideboard). `null` = no cap
   *  to show, used for Limited sideboards (which have no size limit). */
  target: number | null;
  entries: DeckEntry[];
  meta: Record<string, CardMeta> | undefined;
  section: Section;
  onDelta: (section: Section, idx: number, delta: number) => void;
  onRemove: (section: Section, idx: number) => void;
}) {
  // Group mainboard by type; sideboard renders flat (players read it
  // as a single list keyed by matchup, not by type).
  const groups = useMemo(() => {
    if (section === "side") return null;
    const buckets = new Map<BucketTitle, Array<{ entry: DeckEntry; idx: number }>>();
    entries.forEach((entry, idx) => {
      const b = bucketFor(entry.name, meta);
      const arr = buckets.get(b) ?? [];
      arr.push({ entry, idx });
      buckets.set(b, arr);
    });
    const ordered: BucketTitle[] = [...TYPE_ORDER, "Other"];
    const out: Array<{
      title: string;
      rows: Array<{ entry: DeckEntry; idx: number }>;
    }> = [];
    for (const b of ordered) {
      const rows = buckets.get(b);
      if (!rows || rows.length === 0) continue;
      rows.sort((a, b2) => {
        const ca = meta?.[a.entry.name.toLowerCase()]?.cmc ?? 99;
        const cb = meta?.[b2.entry.name.toLowerCase()]?.cmc ?? 99;
        if (ca !== cb) return ca - cb;
        return (
          b2.entry.count - a.entry.count ||
          a.entry.name.localeCompare(b2.entry.name)
        );
      });
      out.push({ title: pluralize(b), rows });
    }
    return out;
  }, [entries, meta, section]);

  const offTarget =
    target === null
      ? false
      : section === "main"
        ? total !== target
        : total > target;

  return (
    <section>
      <header className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-semibold tracking-wide text-fg-muted uppercase">
          {title}
        </h2>
        <span
          className={cn(
            "font-mono text-xs tabular-nums",
            offTarget ? "text-warn" : "text-fg-muted",
          )}
        >
          {target === null ? total : `${total} / ${target}`}
        </span>
      </header>
      <div className="surface p-4 space-y-5">
        {entries.length === 0 && (
          <div className="text-xs text-fg-muted">(empty)</div>
        )}
        {groups ? (
          groups.map((g) => (
            <div key={g.title}>
              <div className="flex items-center justify-between mb-1.5">
                <h3 className="text-[11px] uppercase tracking-wider text-fg-subtle font-medium">
                  {g.title}
                </h3>
                <span className="font-mono text-[11px] text-fg-subtle tabular-nums">
                  {g.rows.reduce((s, r) => s + r.entry.count, 0)}
                </span>
              </div>
              {g.rows.map((r) => (
                <EditableRow
                  key={`${r.entry.name}-${r.idx}`}
                  entry={r.entry}
                  meta={meta}
                  onDec={() => onDelta(section, r.idx, -1)}
                  onInc={() => onDelta(section, r.idx, +1)}
                  onRemove={() => onRemove(section, r.idx)}
                />
              ))}
            </div>
          ))
        ) : (
          entries.map((e, idx) => (
            <EditableRow
              key={`${e.name}-${idx}`}
              entry={e}
              meta={meta}
              onDec={() => onDelta(section, idx, -1)}
              onInc={() => onDelta(section, idx, +1)}
              onRemove={() => onRemove(section, idx)}
            />
          ))
        )}
      </div>
    </section>
  );
}

function EditableRow({
  entry,
  meta,
  onDec,
  onInc,
  onRemove,
}: {
  entry: DeckEntry;
  meta: Record<string, CardMeta> | undefined;
  onDec: () => void;
  onInc: () => void;
  onRemove: () => void;
}) {
  const m = meta?.[entry.name.toLowerCase()];
  const limit = cardCopyLimit(m ?? { name: entry.name });
  const maxed = !!m && entry.count >= limit;
  return (
    <div className="group grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-3 py-1 font-mono text-[13px] tabular-nums">
      <div className="flex items-center gap-0.5">
        <button
          onClick={onDec}
          className="inline-flex items-center justify-center h-6 w-6 rounded text-fg-subtle hover:text-fg hover:bg-bg-raised/60"
          aria-label={`decrease ${entry.name}`}
        >
          <Minus size={12} strokeWidth={2} />
        </button>
        <span className="w-5 text-center text-fg-muted">{entry.count}</span>
        <button
          onClick={onInc}
          disabled={maxed}
          className={cn(
            "inline-flex items-center justify-center h-6 w-6 rounded",
            maxed
              ? "text-fg-subtle/40 cursor-not-allowed"
              : "text-fg-subtle hover:text-fg hover:bg-bg-raised/60",
          )}
          aria-label={`increase ${entry.name}`}
          title={maxed ? `${limit} copies max` : undefined}
        >
          <Plus size={12} strokeWidth={2} />
        </button>
      </div>
      <div className="truncate leading-none">
        <CardHover name={entry.name}>{entry.name}</CardHover>
      </div>
      <div className="flex items-center justify-end min-w-[3.25rem]">
        <ManaCost cost={m?.mana_cost} />
      </div>
      <SetSymbol code={entry.set ?? null} />
      <button
        onClick={onRemove}
        className="inline-flex items-center justify-center h-6 w-6 rounded text-fg-subtle/60 opacity-0 group-hover:opacity-100 hover:text-danger hover:bg-bg-raised/60 focus:opacity-100"
        aria-label={`remove ${entry.name}`}
      >
        <X size={12} strokeWidth={2} />
      </button>
    </div>
  );
}

// ── Live stats rail ──────────────────────────────────────────────

function LiveStats({
  draft,
  meta,
  serverValidation,
  cheapErrors,
  serverFresh,
  limited,
}: {
  draft: Deck;
  meta: Record<string, CardMeta> | undefined;
  serverValidation: DeckValidationResult | undefined;
  cheapErrors: string[];
  serverFresh: boolean;
  /** Inferred Limited (40-card) deck — flips the rail's count targets
   *  from 60/15 to 40/no-cap. Source-of-truth lives in the parent. */
  limited: boolean;
}) {
  const mainTotal = sum(draft.mainboard);
  const sideTotal = sum(draft.sideboard);

  // Client-computed curve/colors/lands from meta. These flicker into
  // place as the batch metadata query resolves — acceptable cost for
  // not round-tripping on every keystroke.
  const { curve, colors, lands, price } = useMemo(() => {
    const curve: Record<number, number> = {};
    const colorSet = new Set<string>();
    let lands = 0;
    let price = 0;
    for (const e of draft.mainboard) {
      const m = meta?.[e.name.toLowerCase()];
      if (!m) continue;
      for (const c of m.colors) colorSet.add(c);
      if (m.type_line.includes("Land")) {
        lands += e.count;
      } else {
        const bucket = Math.min(7, Math.round(m.cmc));
        curve[bucket] = (curve[bucket] ?? 0) + e.count;
      }
      if (typeof m.price_usd === "number") price += m.price_usd * e.count;
    }
    for (const e of draft.sideboard) {
      const m = meta?.[e.name.toLowerCase()];
      if (!m) continue;
      if (typeof m.price_usd === "number") price += m.price_usd * e.count;
    }
    return {
      curve,
      colors: Array.from(colorSet).sort(),
      lands,
      price,
    };
  }, [draft, meta]);

  // Source of truth for legality display: server when fresh, cheap
  // client check otherwise. Ordering matters — the cheap check lacks
  // card-not-found and format-illegal errors, so a deck the server
  // rejects can look green client-side until the debounce fires.
  const errorsToShow = serverFresh
    ? (serverValidation?.errors ?? [])
    : cheapErrors;
  const warningsToShow = serverFresh
    ? (serverValidation?.warnings ?? [])
    : [];
  const legal = errorsToShow.length === 0;

  return (
    <div className="surface p-4 space-y-4 text-[13px]">
      <div>
        <div className="eyebrow mb-2">Live</div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono tabular-nums text-xs">
          <div className="text-fg-subtle">main</div>
          <div
            className={cn(
              (limited ? mainTotal === 40 : mainTotal === 60)
                ? "text-fg"
                : "text-warn",
            )}
          >
            {mainTotal} / {limited ? 40 : 60}
          </div>
          <div className="text-fg-subtle">side</div>
          <div
            className={cn(
              limited || sideTotal <= 15 ? "text-fg" : "text-warn",
            )}
          >
            {limited ? sideTotal : `${sideTotal} / 15`}
          </div>
          <div className="text-fg-subtle">lands</div>
          <div className="text-fg">{lands}</div>
          <div className="text-fg-subtle">colors</div>
          <div className="text-fg">
            {colors.length > 0 ? colors.join("") : "—"}
          </div>
          <div className="text-fg-subtle">price</div>
          <div className="text-fg">
            {price > 0 ? `$${price.toFixed(0)}` : "—"}
          </div>
        </div>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wider text-fg-subtle mb-1.5">
          Curve
        </div>
        <ManaCurve curve={curve} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[11px] uppercase tracking-wider text-fg-subtle">
            Legality
          </div>
          <span
            className={cn(
              "text-[11px] font-mono",
              legal ? "text-accent" : "text-danger",
            )}
            title={serverFresh ? "Server-verified" : "Local check — server will verify"}
          >
            {legal ? "ok" : "errors"}
            {!serverFresh && legal ? "…" : ""}
          </span>
        </div>
        {errorsToShow.length === 0 && warningsToShow.length === 0 && (
          <div className="text-xs text-fg-subtle">No issues.</div>
        )}
        {errorsToShow.length > 0 && (
          <ul className="text-xs text-danger space-y-0.5 list-disc pl-4">
            {errorsToShow.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        )}
        {warningsToShow.length > 0 && (
          <ul className="text-xs text-warn space-y-0.5 list-disc pl-4 mt-1">
            {warningsToShow.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// Header surfaced when the editor was opened via the Apply CTA on
// DeckDetail. Walks the user through the four states: extractor
// loading, extractor failed, no actionable edits found, and
// edits-applied (the success path). Doesn't gate any controls — the
// editor is fully usable while the extractor is still running, the
// user just won't see the proposed_deck pre-fill until it lands.
function ApplyAskBanner({
  loading,
  error,
  editsCount,
  unmapped,
}: {
  loading: boolean;
  error: Error | null;
  editsCount: number;
  unmapped: string;
}) {
  if (loading) {
    return (
      <div className="surface p-3 text-xs text-fg-muted flex items-center gap-2">
        <MessageSquare size={13} strokeWidth={1.75} />
        Extracting suggested edits from the Ask answer…
      </div>
    );
  }
  if (error) {
    return (
      <div className="surface p-3 text-xs text-danger">
        Couldn't extract edits: {error.message}. You can still edit
        manually below.
      </div>
    );
  }
  if (editsCount === 0) {
    return (
      <div className="surface p-3 text-xs text-warn space-y-1">
        <div className="font-medium inline-flex items-center gap-1.5">
          <MessageSquare size={13} strokeWidth={1.75} />
          No concrete card swaps found in the Ask answer.
        </div>
        {unmapped && (
          <div className="text-fg-muted leading-relaxed">{unmapped}</div>
        )}
      </div>
    );
  }
  return (
    <div className="surface p-3 text-xs text-accent space-y-1">
      <div className="font-medium inline-flex items-center gap-1.5">
        <MessageSquare size={13} strokeWidth={1.75} />
        Pre-filled with {editsCount} suggested edit
        {editsCount === 1 ? "" : "s"} from your Ask. Review below
        and adjust before saving.
      </div>
      {unmapped && (
        <div className="text-fg-muted leading-relaxed">{unmapped}</div>
      )}
    </div>
  );
}
